import { useCallback, useSyncExternalStore } from "react";
import { HAELT_MS, merklisteLeeren } from "./merkliste";
import { zeitVorgabe, VORGABE_MIETTAGE, type Zeitart, type Zeitwunsch } from "../bausteine/Zeitfrage";
import { heuteImBetrieb } from "./betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Die gestellte Frage der Vermietfläche — sie bleibt stehen.

   Luca, 17.09.2026: *„Mach, dass die Vermieten-Maske immer so bleibt,
   es sei denn man macht einen Reset, weißte?"*

   Der Fall dahinter ist der Anruf: „Habt ihr nächste Woche einen
   Radlader?" — „Moment, für welche Baustelle war das noch mal?" Wer
   dafür in die Kundenakte springt und zurückkommt, hat bis hierher eine
   leere Maske vorgefunden und tippte alles noch einmal. Die Sammlung
   blieb schon immer (`daten/merkliste.ts`); die **Frage** fiel weg — und
   damit genau der Teil, der die Antwort erst erzeugt.

   ── Was hier steht und was nicht ─────────────────────────────────

   Hier steht die Frage: **Suchwort, Gerätegruppe, Zeitfrage** (Form und
   Werte). **Kunde und Einsatzort stehen nicht hier**, sondern in der
   Sammlung — sie gehören dem Entwurf und nicht der Maske, und sie sind
   dort schon seit dem 17.09. vormittags haltbar abgelegt.

   **Was nicht bleibt: aufgeklappte Zeilen und offene Blätter.** Ein
   Blatt, das beim Betreten der Maske von selbst aufgeht, ist keine
   Erinnerung, sondern ein Schreck — sie stehen deshalb weiter im
   `useState` der Maske und nicht hier.

   ── Dieselbe Mechanik wie die Sammlung ───────────────────────────

   Browser, nicht Datenbank; `useSyncExternalStore`; jeder Zugriff in
   `try/catch`; **dieselbe Frist** — `HAELT_MS` wird aus
   `daten/merkliste.ts` importiert und nicht noch einmal aufgeschrieben.
   Zwölf Stunden stehen damit an einer Stelle im Code, und wenn sie sich
   ändern, ändern sie sich für beides.

   ── Der Tag von gestern fällt weg, das Suchwort nicht ────────────

   Innerhalb der Frist kann ein Stand über Mitternacht reichen: wer um
   20 Uhr fragt und um 7 Uhr wiederkommt, ist elf Stunden später da —
   und sein „ab morgen" heißt dann **heute**, sein „ab heute" gestern.

   **Ein Datum, das vor heute liegt, wird deshalb nicht wiederhergestellt,
   sondern zurückgesetzt** (`zeitVorgabe()` — ab morgen, fünf Miettage).
   Der Grund ist nicht Ordnung, sondern Haftung: Für einen begonnenen
   Zeitraum stimmt weder der Preis (die Staffel zählt ab dem ersten Tag)
   noch die Lage (`GET /nachschlagen` antwortet über Tage, die vorbei
   sind). Ein sichtbar falsches Datum wäre die zweite Möglichkeit — aber
   es steht klein in einem Feld, das man beim Wiederkommen gerade nicht
   ansieht, und der Preis daneben steht groß und liest sich richtig.

   **Suchwort und Gruppe bleiben trotzdem.** Sie altern nicht: „Radlader"
   heißt morgen dasselbe. Wer sie mit dem Datum zusammen wegwürfe,
   nähme dem Wiederkommen den Sinn.
   ══════════════════════════════════════════════════════════════════ */

const FACH = "mrmachine.vermieten";

/** Mehr tippt niemand in ein Suchfeld — und was länger ist, ist ein Unfall. */
const WORT_HOECHSTENS = 120;

/** Die Frage, wie die Fläche sie stellt. */
export type Vermietstand = {
  wort: string;
  /** Die gewählte Gerätegruppe, `null` heißt keine. */
  gruppe: string | null;
  wunsch: Zeitwunsch;
  /** Wann zuletzt etwas gefragt wurde. Was älter ist als `HAELT_MS`, fällt weg. */
  standAm: number;
};

export function leererStand(): Vermietstand {
  return { wort: "", gruppe: null, wunsch: zeitVorgabe(), standAm: Date.now() };
}

/* ══════════════ Der Speicher ══════════════ */

const txt = (wert: unknown): string => (typeof wert === "string" ? wert : "");
const zahl = (wert: unknown, vorgabe = 0): number => {
  const n = typeof wert === "number" ? wert : Number(wert);
  return Number.isFinite(n) ? n : vorgabe;
};

/** Ein Tag im Format der Anwendung — `2026-09-18`, und sonst nichts. */
const istTag = (wert: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(wert);

/** Was aus dem Speicher kommt, ist fremd — es wird gelesen, nicht geglaubt. */
function alsWunsch(roh: unknown): Zeitwunsch | null {
  if (!roh || typeof roh !== "object") return null;
  const x = roh as Record<string, unknown>;
  const art = txt(x["art"]);
  const von = txt(x["von"]);
  if (!istTag(von)) return null;
  if (art !== "kw" && art !== "spanne" && art !== "dauer") return null;
  const bis = txt(x["bis"]);
  /* **Der Tag von gestern kommt nicht wieder.** Der Grund steht oben im
     Kopf dieser Datei. Geprüft wird der Anfang: Läge nur das Ende in der
     Vergangenheit, wäre der Zeitraum ohnehin ganz vorbei. */
  if (von < heuteImBetrieb()) return null;
  return {
    art: art as Zeitart,
    von,
    bis: istTag(bis) && bis >= von ? bis : null,
    tage: Math.min(366, Math.max(1, Math.round(zahl(x["tage"], VORGABE_MIETTAGE)))),
  };
}

function ausSpeicher(): Vermietstand {
  try {
    const roh = window.localStorage.getItem(FACH);
    if (!roh) return leererStand();
    const x = JSON.parse(roh) as Record<string, unknown>;
    const standAm = zahl(x["standAm"]);
    if (standAm < Date.now() - HAELT_MS) return leererStand();
    const gruppe = txt(x["gruppe"]);
    return {
      wort: txt(x["wort"]).slice(0, WORT_HOECHSTENS),
      gruppe: gruppe || null,
      /* Fällt das Datum weg, bleibt der Rest: Die Frage „welcher Radlader"
         ist morgen dieselbe, nur der Zeitraum ist es nicht. */
      wunsch: alsWunsch(x["wunsch"]) ?? zeitVorgabe(),
      standAm,
    };
  } catch {
    return leererStand();
  }
}

/* Ein Stand im Speicher der Seite, nicht nur im Speicher des Browsers —
   dieselbe Überlegung wie bei der Sammlung: Die Maske und ihr Reset sind
   zwei Bäume, und `useSyncExternalStore` ist der Weg, den React dafür
   vorsieht. */
let stand: Vermietstand | null = null;
const hoerer = new Set<() => void>();

function jetzt(): Vermietstand {
  if (stand === null) stand = ausSpeicher();
  return stand;
}

function setzen(neu: Vermietstand): void {
  stand = neu;
  try {
    window.localStorage.setItem(FACH, JSON.stringify(stand));
  } catch {
    /* Kein Platz, kein Recht, keine Ablage — die Frage steht trotzdem,
       solange der Reiter offen ist. */
  }
  hoerer.forEach((ruf) => ruf());
}

function anmelden(ruf: () => void): () => void {
  hoerer.add(ruf);
  const fremd = (e: StorageEvent) => {
    if (e.key !== null && e.key !== FACH) return;
    stand = ausSpeicher();
    hoerer.forEach((x) => x());
  };
  window.addEventListener("storage", fremd);
  return () => {
    hoerer.delete(ruf);
    window.removeEventListener("storage", fremd);
  };
}

/** Auf dem Server gibt es keinen Speicher. Einmal beim Laden gerechnet und
 *  nie wieder: Der Rückgabewert darf sich nicht bei jedem Aufruf ändern,
 *  sonst zeichnet `useSyncExternalStore` endlos. */
const LEER: Vermietstand = leererStand();

/* ══════════════ Die Handgriffe ══════════════ */

export function wortSetzen(wort: string): void {
  setzen({ ...jetzt(), wort: wort.slice(0, WORT_HOECHSTENS), standAm: Date.now() });
}

export function gruppeSetzen(gruppe: string | null): void {
  setzen({ ...jetzt(), gruppe, standAm: Date.now() });
}

export function wunschSetzen(wunsch: Zeitwunsch): void {
  setzen({ ...jetzt(), wunsch, standAm: Date.now() });
}

/**
 * Der Reset — die Frage **und** die Sammlung.
 *
 * Luca: *„es sei denn man macht einen Reset"*. Ein Reset, der die Frage
 * leert und die drei gemerkten Posten stehen lässt, ist keiner: Der
 * nächste Anrufer bekäme die Geräte des vorigen zu sehen. `merklisteLeeren`
 * nimmt dabei auch Kunde und Einsatzort mit — das steht dort seit dem
 * Vormittag so und aus demselben Grund.
 */
export function vermietstandZuruecksetzen(): void {
  setzen(leererStand());
  merklisteLeeren();
}

/**
 * **Nach dem Abschluss fängt die nächste Frage leer an** (Systemprüfung A-06,
 * 24.09.2026).
 *
 * Lisa: *„Direkt nach einem vorherigen Auftrag ein neues Gerät für nur einen
 * Tag angefragt. Die Felder waren vorbefüllt und haben den Zeitraum des
 * vorherigen Auftrags übernommen."* Die Frage bleibt stehen, solange der
 * Anruf läuft (Luca, 17.09.2026) — mit „Jetzt vermieten" oder „Angebot
 * erstellen" ist er vorbei. Der nächste Anrufer bekommt die Vorgabe, nicht
 * den Zeitraum des vorigen. Die Sammlung leert der Abschluss selbst.
 */
export function frageNachAbschlussLeeren(): void {
  setzen(leererStand());
}

/* ══════════════ Der Haken ══════════════ */

/**
 * Die gestellte Frage, wie die Vermietfläche sie braucht.
 *
 * ```tsx
 * const { wort, gruppe, wunsch, setzeWort } = useVermietstand();
 * ```
 */
export function useVermietstand() {
  const gelesen = useSyncExternalStore(anmelden, jetzt, () => LEER);
  const setzeWort = useCallback(wortSetzen, []);
  const setzeGruppe = useCallback(gruppeSetzen, []);
  const setzeWunsch = useCallback(wunschSetzen, []);
  const zuruecksetzen = useCallback(vermietstandZuruecksetzen, []);
  return {
    wort: gelesen.wort,
    gruppe: gelesen.gruppe,
    wunsch: gelesen.wunsch,
    setzeWort,
    setzeGruppe,
    setzeWunsch,
    zuruecksetzen,
    /** Steht überhaupt etwas da? Der Reset fragt sonst nach nichts. */
    gefuellt: gelesen.wort.trim().length > 0 || gelesen.gruppe !== null,
  };
}
